import { ASSOCIATED_TOKEN_PROGRAM_ID, NATIVE_MINT, TOKEN_PROGRAM_ID, Token } from '@solana/spl-token';
import { Blockhash, Connection, PublicKey, SystemProgram, Transaction } from '@solana/web3.js';

async function getWSolATA(connection: Connection, publicKey: PublicKey) {
  const wSolAddress = await Token.getAssociatedTokenAddress(
    ASSOCIATED_TOKEN_PROGRAM_ID,
    TOKEN_PROGRAM_ID,
    NATIVE_MINT,
    publicKey,
    true,
  );
  const accountInfo = await connection.getAccountInfo(wSolAddress);
  return { address: wSolAddress, accountInfo };
}

interface CreateTxParams {
  connection: Connection;
  publicKey: PublicKey;
  blockhash: Blockhash;
  lastValidBlockHeight: number;
}

async function createAndTransferWSOLTransaction({
  connection,
  publicKey,
  blockhash,
  lastValidBlockHeight,
  amountToTransferInLamports,
}: CreateTxParams & { amountToTransferInLamports: number }) {
  const wSolAccount = await getWSolATA(connection, publicKey);
  const instructions = [];

  // If no accountInfo, create one
  if (!wSolAccount.accountInfo) {
    // Create ATA account
    instructions.push(
      Token.createAssociatedTokenAccountInstruction(
        ASSOCIATED_TOKEN_PROGRAM_ID,
        TOKEN_PROGRAM_ID,
        NATIVE_MINT,
        wSolAccount.address,
        publicKey,
        publicKey,
      ),
    );
  }

  // Fund account and sync
  instructions.push(
    SystemProgram.transfer({
      fromPubkey: publicKey,
      toPubkey: wSolAccount.address,
      lamports: amountToTransferInLamports,
    }),
  );
  instructions.push(
    // This is not exposed by the types, but indeed it exists
    (Token as any).createSyncNativeInstruction(TOKEN_PROGRAM_ID, wSolAccount.address),
  );

  const transaction = new Transaction({ feePayer: publicKey, blockhash, lastValidBlockHeight });
  transaction.add(...instructions);
  return transaction;
}

async function createUnwrapSolTransaction({ connection, publicKey, blockhash, lastValidBlockHeight }: CreateTxParams) {
  const wSolAccount = await getWSolATA(connection, publicKey);
  const instructions = [];

  if (!wSolAccount.accountInfo) {
    return;
  }
  // Close account
  instructions.push(
    Token.createCloseAccountInstruction(TOKEN_PROGRAM_ID, wSolAccount.address, publicKey, publicKey, []),
  );

  const transaction = new Transaction({ feePayer: publicKey, blockhash, lastValidBlockHeight });
  transaction.add(...instructions);
  return transaction;
}

export { createAndTransferWSOLTransaction, createUnwrapSolTransaction };
